import { IconButton, styled, Typography } from "@mui/material";
import PlusIcon from "assets/icons/PlusIcon";
import { StyledAvatar } from "components/Shared/Dashboard/styled";
import { useFormikContext } from "formik";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { excelDataReset, excelSetData } from "../../store/actions/excelAction";
import { apiSetAlert } from "../../api/appApi";
import { resetForm } from "../../store/actions/formAction";

const xlsx = require("xlsx");

const backgroundImage =
  "data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='%23AEAEAEFF' strokeWidth='4' stroke-dasharray='14 8' stroke-dashoffset='32' stroke-linecap='square'/%3e%3c/svg%3e";
const roundedBackgroundImage =
  "data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='83' ry='83' stroke='%23AEAEAEFF' strokeWidth='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e";
// @ts-ignore
const ButtonContainer = styled("div")(
  ({ width, height, rounded }: { width: number | string; height: number | string; rounded?: boolean }) => ({
    width: width,
    position: "relative",
    height: "84%",
    borderRadius: rounded ? "50%" : "4px",
    backgroundImage: `url("${rounded ? roundedBackgroundImage : backgroundImage}")`,
    backgroundSize: "100% 100%",
    backgroundColor: "unset",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  })
);
const InnerButton = styled("div")(
  ({
    rounded,
    width,
    height,
    horizontal,
    hovered
  }: {
    width: number | string;
    height: number | string;
    horizontal?: boolean;
    rounded?: boolean;
    hovered?: boolean;
  }) => ({
    width: width,
    height: height,
    borderRadius: rounded ? "50%" : "4px",
    backgroundColor: "#F5F5F5",
    display: "flex",
    flexDirection: horizontal ? "row" : "column",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: hovered ? "#e4e3e3 0px 0px 20px 5px" : "unset"
  })
);

interface roundedInterface {
  rounded?: boolean;
}

const ProgressContainer = styled("div")<roundedInterface>(({ theme, rounded }) => ({
  width: "100%",
  height: "100%",
  borderRadius: rounded ? "50%" : "unset",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#72717158"
}));
const Layer = styled("div")<roundedInterface>(({ theme, rounded }) => ({
  width: "100%",
  height: "100%",
  borderRadius: rounded ? "50%" : "unset",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#ffffff90"
}));
const AddButton = styled("div")(({ theme }) => ({
  width: "40px",
  height: "40px",
  backgroundColor: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  cursor: "pointer"
}));
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  bottom: "-20px",
  borderRadius: "0%",
}));

const getInitialImage = (name: string, values: any) => {
  try {
    const image = values[name];
    return image;
  } catch {
    return "";
  }
};

const UploadExcelComponent = ({
  title,
  name,
  width,
  height,
  innerWidth,
  innerHeight,
  LogoPlaceholder,
  horizontal,
  rounded,
  disableLayer
}: {
  title?: string;
  name: string;
  width: number | string;
  height: number | string;
  innerWidth: number | string;
  innerHeight: number | string;
  LogoPlaceholder: React.FC;
  horizontal?: boolean;
  rounded?: boolean;
  disableLayer?: boolean;
}) => {
  const formikContext = useFormikContext();

  const { values } = formikContext;

  const [selectedFile, setSelectedFile] = useState<undefined | File>();
  const [preview, setPreview] = useState<undefined | string>(getInitialImage(name, values));

  const [hovered, setHovered] = useState(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const dispatch = useDispatch();

  const callBack = (url: string) => {
    setIsUploading(false);
    return formikContext.setFieldValue(name, url);
  };

  //@ts-ignore
  const { token } = useSelector((state) => state).businessReducer.business;

  useEffect(() => {
    dispatch(resetForm());
    dispatch(excelDataReset());
  }, []);

  useEffect(() => {
    if (!selectedFile) {
      // setPreview(undefined)
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const onSelectFile = (e: any) => {
    const target = e.target as HTMLInputElement;
    const excelFormats = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ];
    if (!target.files || target.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setSelectedFile(target.files[0]);
    // @ts-ignore
    const excelData = [];
    // @ts-ignore
    for (let i = 0; i < target.files.length; i++) {
      // @ts-ignore
      const file = target.files[i];
      // @ts-ignore
      if (file && excelFormats.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          // @ts-ignore
          const data = e.target.result;
          const workbook = xlsx.read(data, { type: "array" });
          let sheets: any = [];

          workbook.SheetNames.forEach((sn: string) => {
            let titles = new Set();
            let jsonSheet = xlsx.utils.sheet_to_json(workbook.Sheets[sn]);
            for (let i = 0; i < jsonSheet.length; i++) {
              Object.keys(jsonSheet[i]).map((key) => titles.add(key));
            }

            sheets.push({ key: sn, titles: Array.from(titles), items: jsonSheet });
          });

          excelData.push({
            key: file.name.split(".")[0],
            label: file.name,
            name: file.name,
            title: file.name,
            // @ts-ignore
            sheets
          });
        };
        // @ts-ignore
        reader.readAsArrayBuffer(e.target.files[i]);
      } else {
        // @ts-ignore
        dispatch(apiSetAlert("فرمت فایل انتخاب شده صحیح نمی باشد", "error"));
      }
    }
    // @ts-ignore
    dispatch(excelSetData(excelData));
  };

  return (
    <ButtonContainer onMouseLeave={() => setHovered(false)} rounded={rounded} width={width} height={height}>
      {!preview && (
        <InnerButton
          hovered={hovered}
          rounded={rounded}
          horizontal={horizontal}
          width={innerWidth}
          height={innerHeight}
        >
          <LogoPlaceholder />

          <div style={{ padding: "9px" }} />
          <Typography sx={{ fontWeight: 500 }} color="primary">
            {title}
          </Typography>
        </InnerButton>
      )}
      {preview && <StyledAvatar sx={{ borderRadius: rounded ? "50%" : "8px" }} src={preview} />}
      {isUploading && (
        <ProgressContainer rounded={rounded}>
          <CircularProgress />
        </ProgressContainer>
      )}
      {!disableLayer && !hovered && !preview && (
        <Layer onMouseEnter={() => setHovered(true)} rounded={rounded} />
      )}
      <StyledIconButton
        color="primary"
        aria-label="upload picture"
        // @ts-ignore
        component="label"
        className={"w-full  h-full"}
        width={"100%"}
        heght={"100%"}
      >
        {/* @ts-ignore */}
        <input
          className={"w-full  h-full opacity-0"}
          width="100%"
          height="100%"
          multiple={true}
          type="file"

          onChange={(e) => onSelectFile(e)}
          onDrop={e => onSelectFile(e)}
          // onDragOver={e => onSelectFile(e)}
          // onDragEnter={e => onSelectFile(e)}
          // onDragLeave={e => onSelectFile(e)}
          accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        />
      </StyledIconButton>
    </ButtonContainer>
  );
};

export default UploadExcelComponent;
